import React from 'react';
import {
  Box,
  Container,
  Typography,
  Stack,
  IconButton,
  Divider,
  Link
} from '@mui/material';
import { styled } from '@mui/material/styles';
import FacebookIcon from '@mui/icons-material/Facebook';
import XIcon from '@mui/icons-material/X';
import YouTubeIcon from '@mui/icons-material/YouTube';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import PublicIcon from '@mui/icons-material/Public';
import InstagramIcon from '@mui/icons-material/Instagram';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';

const FooterContainer = styled(Box)(({ theme }) => ({
  background: '#455b40',
  color: '#FFFFFF',
  position: 'relative'
}));

const FooterTop = styled(Box)(({ theme }) => ({
  padding: '60px 0 40px'
}));

const FooterBottom = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(0, 0, 0, 0.2)',
  padding: '20px 0'
}));

const LogoContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  marginBottom: '24px'
}));

const LogoImage = styled('img')(({ theme }) => ({
  width: '100px',
  height: '60px',
  marginRight: '16px'
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  marginBottom: '24px',
  color: '#FFFFFF'
}));

const FooterLink = styled(Link)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.8)',
  textDecoration: 'none',
  display: 'block',
  marginBottom: '12px',
  transition: 'color 0.3s ease',
  '&:hover': {
    color: theme.palette.secondary.main,
    textDecoration: 'none'
  }
}));

const ContactItem = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  marginBottom: '16px',
  '& svg': {
    color: '#FFFFFF',
    marginRight: '12px',
    fontSize: '20px'
  }
}));

const SocialButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  color: '#FFFFFF',
  margin: '0 8px 8px 0',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: '#FFFFFF',
    color: theme.palette.primary.dark,
    transform: 'translateY(-3px)'
  }
}));

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  return (
    <FooterContainer>
      <FooterTop>
        <Container maxWidth="lg">
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} flexWrap="wrap">
            {/* Company Info */}
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 35%' }, minWidth: '250px' }}>
              <LogoContainer>
                <LogoImage src="/EkkoFood (4).png" alt="EKKOFOOD Logo" />
                <Typography variant="h4" sx={{ fontFamily: '"Lato", serif', fontWeight: 700 }}>
                  EKKOFOOD
                </Typography>
              </LogoContainer>
              
              <Typography variant="body2" sx={{ 
                color: 'rgba(255, 255, 255, 0.8)', 
                mb: 3,
                lineHeight: 1.6
              }}>
                Comprometidos con la tierra y la salud, ofrecemos alimentos orgánicos frescos y accesibles, y formación en agricultura ecológica.
              </Typography>
              
              <Stack direction="row" flexWrap="wrap">
                <SocialButton component="a" href="https://www.instagram.com/ekkofoodfarm/" target="_blank">
                  <InstagramIcon />
                </SocialButton>
                <SocialButton component="a" href="https://www.facebook.com/ekkofood" target="_blank">
                  <FacebookIcon />
                </SocialButton>
                <SocialButton component="a" href="https://www.youtube.com/@EkkoFood" target="_blank">
                  <YouTubeIcon />
                </SocialButton>
                <SocialButton component="a" href="https://x.com/Ekkofood" target="_blank">
                  <XIcon />
                </SocialButton>
                <SocialButton component="a" href="https://www.tiktok.com/@ekkofood" target="_blank">
                  <PublicIcon />
                </SocialButton>
                <SocialButton component="a" href="https://www.linkedin.com/company/ekkofood" target="_blank">
                  <LinkedInIcon />
                </SocialButton>
                <SocialButton component="a" href="https://www.threads.com/@ekkofoodfarm" target="_blank">
                  <PublicIcon />
                </SocialButton>
              </Stack>
            </Box>

            {/* Links */}
            <Box sx={{ flex: { xs: '1 1 45%', md: '1 1 15%' }, minWidth: '150px' }}>
              <SectionTitle variant="h6">
                Links
              </SectionTitle>
              <FooterLink href="/">Inicio</FooterLink>
              <FooterLink href="/quienes-somos">Quiénes Somos</FooterLink>
              <FooterLink href="/cursos">Cursos</FooterLink>
              <FooterLink href="/noticias-blog">Noticias & Blog</FooterLink>
              <FooterLink href="/contacto">Contacto</FooterLink>
            </Box>

            {/* Contact Info */}
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 35%' }, minWidth: '250px' }}>
              <SectionTitle variant="h6">
                Síguenos de cerca
              </SectionTitle>
              
              <ContactItem>
                <LocationOnIcon />
                <Typography 
                  variant="body2" 
                  sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
                  component="a"
                  href="https://maps.app.goo.gl/KuV4hBej2BeeZqxo8"
                  target="_blank"
                  style={{ textDecoration: 'none' }}
                >
                  C/ Las Lomas s/n. Bétera, Valencia, España
                </Typography>
              </ContactItem>
              
              <ContactItem>
                <PhoneIcon />
                <Typography 
                  variant="body2" 
                  sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
                  component="a"
                  href="tel:+34684238458"
                  style={{ textDecoration: 'none' }}
                >
                  +34 684 238 458
                </Typography>
              </ContactItem>
              
              <ContactItem>
                <EmailIcon />
                <Typography 
                  variant="body2" 
                  sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
                  component="a"
                  href="mailto:info@ekkofood.com"
                  style={{ textDecoration: 'none' }}
                >
                  info@ekkofood.com
                </Typography>
              </ContactItem>
            </Box>
          </Stack>
        </Container>
      </FooterTop>

      <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />
      
      <FooterBottom>
        <Container maxWidth="lg">
          <Stack direction={{ xs: 'column', md: 'row' }} alignItems="center" justifyContent="space-between" spacing={2}>
            <Typography variant="body2" sx={{ 
              color: 'rgba(255, 255, 255, 0.7)',
              textAlign: { xs: 'center', md: 'left' }
            }}>
              © {currentYear} EKKOFOOD. All rights reserved.
            </Typography>
            
          </Stack>
        </Container>
      </FooterBottom>
    </FooterContainer>
  );
};

export default Footer;